import ButtonWithTooltip from "@/_components/ButtonWithTooltip"
import CustomButton from "@/_components/CustomButton"
import { loc } from "@/_services/localization"
import { addOops } from "@/_services/notification"
import { getUiState, setUiState } from "@/_services/uiState"
import { getUsername } from "@/_services/userConfiguration"
import { findJsonPathLine, jsonPathToTokens, localStorageKeys, toggleWrapCommand } from "@/_services/utils"
import axios from "axios"
import { lowerCaseFirstLetter, toQueryString } from "basikon-common-utils"
import JSON5 from "json5"
import React, { Suspense } from "react"
import AceEditor from "react-ace"
import { ButtonGroup, Col, Modal, Row } from "react-bootstrap"

// WARNING: these imports must come after the AceEditor, otherwise webpack throws an error
import "ace-builds/src-noconflict/ext-searchbox"
import "ace-builds/src-noconflict/mode-json"
import "ace-builds/src-noconflict/theme-github"
import "ace-builds/webpack-resolver"

const JsonViewerComponent = React.lazy(() => import("@/_components/JsonViewerComponent"))

function convertToJs(entity) {
  const string = JSON5.stringify(entity, { space: 2, quote: '"' })

  // this block is a hack to group colProps in one line. Can format way better but this works for me
  try {
    const json = string.split("\n")
    const json2 = []
    for (let i = 0; i < json.length; i++) {
      let line = json[i]
      const tline = line.trim()
      if (tline === "colProps: {" || /props_.*: {/.test(tline)) {
        while (++i < json.length) {
          const line2 = json[i].trim()
          if (line2 === "}" || line2 === "},") {
            line = line.substring(0, line.length - 1) // remove trailing comma
            line += " " + line2.trim()
            break
          } else {
            line += " " + line2.trim()
          }
        }
      }
      json2.push(line)
    }
    return json2.join("\n")
  } catch (error) {
    console.error(error.stack)
  }

  return string
}

function convertToJson(entity) {
  return JSON.stringify(entity, null, 2)
}

export default class EntityModelModal extends React.Component {
  constructor(props) {
    super(props)

    // recall whether the viewer was shown last time (the viewer forces JSON on)
    const showViewer = getUiState(localStorageKeys.UI_STATE.JSON_VIEWER.SHOW) === true
    this.state = {
      useJson: showViewer,
      useViewer: showViewer,
    }
  }

  toggleViewer = () => {
    const useViewer = !this.state.useViewer
    this.setState({ useJson: true, useViewer })
    setUiState(localStorageKeys.UI_STATE.JSON_VIEWER.SHOW, useViewer)
  }

  componentDidMount() {
    const { entity, showEntityFieldsButtons } = this.props

    // Select first model field button
    if (showEntityFieldsButtons) {
      const [firstKey] = Object.keys(entity).filter(it => entity[it])
      if (firstKey) this.setSelector(firstKey)
    }
  }

  checkScriptName = async scriptName => {
    if (!scriptName) return

    const isScriptNameTyped = scriptName.includes("Workflow-") || scriptName.includes("-layout-")
    if (isScriptNameTyped) {
      const untypedScriptName = scriptName.replace(/^(\w+Workflow)-\w+$/, "$1").replace(/^(\w+-layout)-\w+$/, "$1")

      let scripts = []
      try {
        const queryString = toQueryString({ name: [scriptName, untypedScriptName], projection: "-_id,name" })
        scripts = (await axios.get(`/api/script/scripts${queryString}`)).data
      } catch (error) {
        addOops(error)
      }

      const script = scripts.find(script => script.name === scriptName)
      if (!script) return untypedScriptName
    }

    return scriptName
  }

  openScript = async (scriptName, query = {}) => {
    if (scriptName) scriptName = await this.checkScriptName(scriptName)

    const queryString = toQueryString({ filter: scriptName, selectedSettingType: "script", name: scriptName, username: getUsername(), ...query })
    window.open(`/scripts${queryString}`)
  }

  setSelector = field => {
    if (this.state.selector === field) {
      this.setState({ selector: undefined })
    } else {
      this.setState({ selector: field })
    }
  }

  // Scroll the ace editor to the node the user clicked in the JSON viewer, honoring array indices.
  scrollEditorToPath = path => {
    const editor = this.editor
    if (!editor) return

    const tokens = jsonPathToTokens(path)
    const line = tokens.length === 0 ? 1 : findJsonPathLine(editor.getValue(), tokens)
    if (line < 1) return

    editor.gotoLine(line, 0, true)
    editor.scrollToLine(line - 1, true, true)
  }

  render() {
    const { entity, close, entityName, pageName, workflowName, showScriptButtons, showEntityFieldsButtons } = this.props
    const { useJson, useViewer, selector } = this.state

    const entityNameLowerCase = lowerCaseFirstLetter(entityName)
    const entityType = entity?.type || entity?.[Object.keys(entity)[0]]?.type
    let firstButton
    if (pageName) {
      firstButton = ["Page", () => this.openScript(pageName)]
    } else {
      let layoutScriptName = entityNameLowerCase + "-layout"
      if (entityType) layoutScriptName += `-${entityType}`

      firstButton = ["Layout", () => this.openScript(layoutScriptName)]
    }

    const buttons = [
      firstButton,
      workflowName && ["Workflow", () => this.openScript(workflowName, { entityType })],
      ["Extension", () => this.openScript("collection-extension", { entityName })],
    ].filter(it => it)

    const displayEntity = selector ? entity[selector] : entity
    const value = useJson ? convertToJson(displayEntity) : convertToJs(displayEntity)
    // keep the viewer and the ace editor the same height
    const panelHeight = "50rem"

    return (
      <Modal show={true} onHide={close} bsSize="large" className="entity-model-modal">
        <Modal.Header closeButton>
          <Modal.Title>{loc`Model`}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="mb-theme">
            <Col xs={12}>
              <ButtonWithTooltip
                className="icn-list-tree icn-sm"
                tooltip={loc("Viewer")}
                bsSize="xsmall"
                bsStyle="default"
                simple={false}
                fill={useViewer}
                onClick={this.toggleViewer}
              />

              {/* while the viewer is on, JSON is forced on, so lock the toggle; it re-enables once the viewer is off */}
              <ButtonWithTooltip
                className="icn-brackets-curly icn-sm"
                tooltip={loc("JSON")}
                bsSize="xsmall"
                bsStyle="default"
                simple={false}
                fill={useJson}
                disabled={useViewer}
                onClick={() => this.setState({ useJson: !useJson })}
              />

              {showEntityFieldsButtons && (
                <ButtonGroup className="btn-group-segmented">
                  {Object.keys(entity)
                    .filter(it => entity[it])
                    .map(entityField => (
                      <CustomButton
                        fill={selector === entityField}
                        bsSize="xs"
                        bsStyle="default"
                        key={entityField}
                        onClick={() => this.setSelector(entityField)}
                      >
                        {entityField}
                      </CustomButton>
                    ))}
                </ButtonGroup>
              )}

              {showScriptButtons &&
                buttons.map(([title, onclick]) => (
                  <CustomButton key={title} pullRight bsSize="xsmall" bsStyle="default" className="flex-center" onClick={onclick}>
                    {loc(title)}
                    <i className="icn-external-link icn-xs ml-5px" />
                  </CustomButton>
                ))}
            </Col>
          </Row>

          <Row>
            <Col xs={4} className={useViewer ? "" : "d-none"}>
              <Suspense fallback={null}>
                <JsonViewerComponent json={value} rootLabel={selector || ""} height={panelHeight} onSelectPath={this.scrollEditorToPath} />
              </Suspense>
            </Col>
            <Col xs={useViewer ? 8 : 12}>
              <AceEditor
                focus={true}
                readOnly
                mode="json"
                theme="github"
                value={value}
                name="entity-model"
                onLoad={editor => (this.editor = editor)}
                showGutter={true}
                editorProps={{ $blockScrolling: true }}
                setOptions={{
                  copyWithEmptySelection: true,
                  useWorker: false, // disable syntax check as it does not support ESNext (async await etc.)
                }}
                width="100%"
                height={panelHeight}
                showPrintMargin={false}
                commands={[toggleWrapCommand]}
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <CustomButton bsSize="sm" onClick={close}>
            {loc`Close`}
          </CustomButton>
        </Modal.Footer>
      </Modal>
    )
  }
}
